import React, { useEffect, useRef, useState } from 'react';

import { IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5';
import * as Yup from 'yup';

import {
  InputMask,
  ButtonMain,
  InputSelect,
  InputText,
  Paragraph,
  SubtitleSecondary,
} from '@components/index';
import { ConfigLabel, ConfigStyles, ConfigValues } from '@config/index';
import { apiRebox, newProviderStorageService } from '@services/index';
import { getValidationErrors } from '@utils/errors';
import {
  formatCellphone,
  formatCNPJ,
  formatCPF,
  formatText,
} from '@utils/formatters';
import { hotToast, toastify } from '@utils/notifiers';
import { validatorEmail } from '@utils/validators';

import { schemaProvider } from './schemaValidation';
import { IProviderData, ITarget } from './typing';

import {
  Container,
  DividingLine,
  FormProvider,
  Sections,
  SectionsGroup,
  SectionsItem,
  SectionsItemGroup,
} from './styles';

interface IProps {
  forNewProvider?: {
    advanceStep(): void;
  };
}

const FromProviderNew: React.FC<IProps> = ({ forNewProvider }) => {
  const formRef = useRef(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [personType, setPersonType] = useState<string>(
    ConfigValues.rebox.user.person_type.physical_person,
  );

  const [alertCheckEmail, setAlertCheckEmail] = useState<number>(0);
  const [alertCheckCpfOrCnpj, setAlertCheckCpfOrCnpj] = useState<number>(0);

  const [providerData, setProviderData] = useState<IProviderData>({
    name: '',
    cpf_cnpj: '',
    email: '',
    cellphone: '',
    status: ConfigValues.rebox.user.status.active,
  });

  const handleGetProviderByEmail = async (email: string) => {
    try {
      const idHotToast = hotToast('Verificando o e-mail', 'loading');
      const { data: responseProvider } = await apiRebox.get(
        `/users?email=${email.toLowerCase()}&role=${
          ConfigValues.rebox.user.role.provider
        }`,
      );

      const { header } = responseProvider;
      if (header.total > 0) {
        hotToast(idHotToast, 'dismiss');
        toastify(
          'Este e-mail não pode ser usado. Já pertence a um prestador cadastrado',
          'error',
        );
        setAlertCheckEmail(2);
      } else {
        setAlertCheckEmail(1);
        hotToast(idHotToast, 'dismiss');
      }
    } catch (error: any) {
      toastify(
        error.response
          ? error.response.data.error
          : 'Houve um error ao tentar verificar o e-mail informado.',
        'error',
      );
      setAlertCheckEmail(2);
    }
  };

  const handleGetProviderByCpfOrCnpj = async (cpf_cnpj: string) => {
    try {
      const query = cpf_cnpj.length === 11 ? 'cpf' : 'cnpj';
      const idHotToast = hotToast(`Verificando o ${query}`, 'loading');
      const { data: responseProvider } = await apiRebox.get(
        `/users?${query}=${cpf_cnpj}&role=${ConfigValues.rebox.user.role.provider}`,
      );
      const { header } = responseProvider;

      if (header.total > 0) {
        setAlertCheckCpfOrCnpj(2);
        hotToast(idHotToast, 'dismiss');
        toastify(
          `Este ${query} não pode ser usado. Já pertence a um prestador cadastrado`,
          'error',
        );
      } else {
        setAlertCheckCpfOrCnpj(1);
        hotToast(idHotToast, 'dismiss');
      }
    } catch (error: any) {
      const query = cpf_cnpj.length === 11 ? 'cpf' : 'cnpj';
      toastify(
        error.response
          ? error.response.data.error
          : `Houve um error ao tentar verificar o ${query} informado.`,
        'error',
      );
      setAlertCheckEmail(2);
    }
  };

  const handleRegisterProvider = async () => {
    try {
      setIsLoading(true);
      await schemaProvider.validate(
        { ...providerData, person_type: personType },
        {
          abortEarly: false,
        },
      );

      const { data: responseCreatedProvider } = await apiRebox.post('/users', {
        name: providerData.name.toLowerCase(),
        person_type: personType,
        date_of_birth: null,
        cpf:
          personType === ConfigValues.rebox.user.person_type.physical_person
            ? formatCPF.removeMask(providerData.cpf_cnpj)
            : null,
        cnpj:
          personType === ConfigValues.rebox.user.person_type.legal_person
            ? formatCNPJ.removeMask(providerData.cpf_cnpj)
            : null,
        sex: null,
        email: providerData.email.toLowerCase(),
        cellphone: formatCellphone.removeMask(providerData.cellphone),
        telephone: null,
        status: providerData.status,
        role: ConfigValues.rebox.user.role.provider,
        company_size: '',
        access_level: ConfigValues.rebox.user.access_level.normal,
        subordinate_of: null,
        id_who_indicated: null,
        is_partner: true,
        accept_terms_of_use: true,
        gateway_curstomers_id: null,
      });

      const { header, data: createdProvider } = responseCreatedProvider;

      toastify(header.message, 'success');
      setIsLoading(false);

      newProviderStorageService.updateProvider({
        id: createdProvider.id,
        name: providerData.name.toLowerCase(),
        cpf_cnpj: formatText.removeAllNonDigits(providerData.cpf_cnpj),
        cellphone: formatCellphone.removeMask(providerData.cellphone),
        status: providerData.status,
        email: providerData.email.toLowerCase(),
        person_type: personType,
      });
      forNewProvider?.advanceStep();
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        setIsLoading(false);
        const errors = getValidationErrors(error);

        const { name, cpf_cnpj, email, cellphone, status } = errors;
        if (name) toastify(name, 'error');
        if (cpf_cnpj) toastify(cpf_cnpj, 'error');
        if (email) toastify(email, 'error');
        if (cellphone) toastify(cellphone, 'error');
        if (status) toastify(status, 'error');
      } else if (error.response) {
        toastify(error.response.data.error, 'error');
      } else {
        toastify('Houve um erro ao tentar cadastrar esse provedor.', 'error');
      }
    }
  };

  const handleProviderData = ({ name, value }: ITarget) => {
    setProviderData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    newProviderStorageService.cleanMany();
  }, []);

  return (
    <Container>
      <FormProvider ref={formRef} onSubmit={() => []} initialData={[]}>
        <Sections>
          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Razão Social/Nome Fantasia
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Informe a razão social e/ou nome fantasia sem abreviações
              </Paragraph>
              <InputText
                name="name"
                placeholder="Informe a razão social"
                tabIndex={1}
                autoFocus
                value={providerData.name.toUpperCase()}
                onChange={({ target }) => handleProviderData(target)}
              />
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Tipo de pessoa
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Define o documento exigido para registro
              </Paragraph>
              <InputSelect
                name="person_type"
                options={ConfigLabel.rebox.others.user.person_type}
                placeholder="Selecione"
                selectedDefault={personType}
                onChange={({ target }) => {
                  setPersonType(target.value);
                  setProviderData(prevData => ({ ...prevData, cpf_cnpj: '' }));
                }}
                tabIndex={2}
              />
            </SectionsItem>
          </SectionsGroup>

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                CPF/CNPJ
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Informe o número do documento do prestador
              </Paragraph>
              <SectionsItemGroup>
                <InputMask
                  name="cpf_cnpj"
                  mask={
                    personType ===
                    ConfigValues.rebox.user.person_type.physical_person
                      ? '999.999.999-99'
                      : '99.999.999/9999-99'
                  }
                  value={providerData.cpf_cnpj}
                  placeholder="Número do documento"
                  tabIndex={3}
                  onChange={({ target }) => {
                    handleProviderData(target);
                  }}
                  onBlur={({ target }) => {
                    const cpf_cnpj = formatText.removeAllNonDigits(
                      target.value,
                    );
                    if (cpf_cnpj.length === 11 || cpf_cnpj.length === 14) {
                      handleGetProviderByCpfOrCnpj(cpf_cnpj);
                    } else if (alertCheckCpfOrCnpj !== 0) {
                      setAlertCheckCpfOrCnpj(0);
                    }
                  }}
                />
                {alertCheckCpfOrCnpj === 1 && (
                  <IoCheckmarkCircle
                    color={ConfigStyles.rebox.colors.greenEmerald.main}
                    size={20}
                  />
                )}
                {alertCheckCpfOrCnpj === 2 && (
                  <IoCloseCircle
                    color={ConfigStyles.rebox.colors.red.main}
                    size={20}
                  />
                )}
              </SectionsItemGroup>
            </SectionsItem>
          </SectionsGroup>

          <DividingLine />

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                E-mail
              </SubtitleSecondary>
              <Paragraph
                textAlign="start"
                nameColor="black"
                fontSize={13}
                opacity={0.5}
                style={{ marginBottom: '2vh' }}
              >
                Informe o endereço de email do prestador
              </Paragraph>
              <SectionsItemGroup>
                <InputText
                  name="email"
                  value={providerData.email}
                  onChange={({ target }) => handleProviderData(target)}
                  onBlur={({ target }) => {
                    const email = target.value.trim();
                    if (validatorEmail.check(email)) {
                      handleGetProviderByEmail(email);
                    } else if (alertCheckEmail !== 2) {
                      setAlertCheckEmail(2);
                    } else if (email === '') {
                      setAlertCheckEmail(0);
                    }
                  }}
                  placeholder="Informe o e-mail"
                  tabIndex={4}
                />
                {alertCheckEmail === 1 && (
                  <IoCheckmarkCircle
                    color={ConfigStyles.rebox.colors.greenEmerald.main}
                    size={20}
                  />
                )}
                {alertCheckEmail === 2 && (
                  <IoCloseCircle
                    color={ConfigStyles.rebox.colors.red.main}
                    size={20}
                  />
                )}
              </SectionsItemGroup>
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Celular/Whatsapp
              </SubtitleSecondary>
              <Paragraph
                textAlign="start"
                nameColor="black"
                fontSize={13}
                opacity={0.5}
                style={{ marginBottom: '2vh' }}
              >
                Informe o celular e/ou whatsapp do prestador
              </Paragraph>
              <InputMask
                name="cellphone"
                value={providerData.cellphone}
                onChange={({ target }) => handleProviderData(target)}
                mask={'+55 (99) 99999-9999'}
                placeholder="Informe o celular"
                tabIndex={5}
              />
            </SectionsItem>
          </SectionsGroup>

          <DividingLine />

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Situação do prestador
              </SubtitleSecondary>
              <Paragraph
                textAlign="start"
                nameColor="black"
                fontSize={13}
                opacity={0.5}
                style={{ marginBottom: '2vh' }}
              >
                Informa a situação atual do prestador
              </Paragraph>
              <InputSelect
                name="status"
                options={ConfigLabel.rebox.others.user.status.filter(
                  status => status.value !== 'LEAD',
                )}
                placeholder="Selecione"
                selectedDefault={ConfigValues.rebox.user.status.active}
                tabIndex={6}
                onChange={({ target }) => handleProviderData(target)}
              />
            </SectionsItem>
          </SectionsGroup>
        </Sections>

        <ButtonMain
          type="submit"
          loading={isLoading}
          style={{
            marginTop: '4vh',
            maxWidth: 250,
            cursor:
              alertCheckCpfOrCnpj === 2 || alertCheckEmail === 2
                ? 'not-allowed'
                : 'pointer',
          }}
          tabIndex={7}
          onClick={event => {
            event.preventDefault();
            handleRegisterProvider();
          }}
          disabled={alertCheckCpfOrCnpj === 2 || alertCheckEmail === 2}
          isDisable={alertCheckCpfOrCnpj === 2 || alertCheckEmail === 2}
        >
          Cadastrar
        </ButtonMain>
      </FormProvider>
    </Container>
  );
};

export default FromProviderNew;
